import { getSDK } from '@/store/sdkStore';
import { commands } from '@/api/commands';

/**
 * Контекст выполнения обработчика формы
 */
export type HandlerContext = 'client' | 'server' | 'both';

/**
 * Параметры обработчика
 */
export interface HandlerParams {
  field?: string;
  value?: unknown;
  data?: Record<string, unknown>;
  row?: Record<string, unknown>;
  tableName?: string;
  [key: string]: unknown;
}

  /**
   * API формы — доступен из модуля формы
   */
  export interface FormAPI {
    // Поля
    getField(name: string): unknown;
    setField(name: string, value: unknown): void;
    setFieldVisible(name: string, visible: boolean): void;

    // Табличные части
    getRow(tableName: string, rowIndex: number): Record<string, unknown> | undefined;
    addRow(tableName: string, data: Record<string, unknown>): void;
    deleteRow(tableName: string, rowIndex: number): void;
    clearTable(tableName: string): void;

    // Пикеры
    openPicker(type: string, callback: (result: { id: string; name: string; objectType: string; objectName: string }) => void): void;

    // Навигация
    setTitle(title: string): void;
    close(): void;
    refresh(): void;

    // Стандартные команды (как в 1С: СтандартнаяОбработка=Ложь — стандарт
    // вызывается явно из своего обработчика команды)
    save(): Promise<void>;
    post(): Promise<void>;

    // Данные формы
    objectType: string;
    objectName: string;
    recordId?: string;
    values: Record<string, unknown>;

    // РегистрСведенийМенеджерЗаписи (как в 1С): чтение/запись записи КОДОМ.
    //   read(registerCode, dimensions) — Прочитать() по измерениям → значения в форму
    //   write(registerCode, values)   — Записать() (upsert по измерениям регистра)
    //   readSet(registerCode, dimensions)  — Набор записей: Прочитать() → массив строк
    //   writeSet(registerCode, records, dimensions?) — Набор записей: Записать()
    //     (полная замена: удаляются записи с теми же измерениями, вставляются новые;
    //     dimensions обязательны при пустом records — очистка набора)
    registerRecord: {
      read(registerCode: string, dimensions: Record<string, unknown>): Promise<Record<string, unknown> | null>;
      write(registerCode: string, values: Record<string, unknown>): Promise<void>;
      readSet(registerCode: string, dimensions: Record<string, unknown>): Promise<Array<Record<string, unknown>>>;
      writeSet(registerCode: string, records: Array<Record<string, unknown>>, dimensions?: Record<string, unknown>): Promise<void>;
    };

    // Вызов СЕРВЕРНОЙ функции модуля ОБЪЕКТА (как в 1С: модуль формы вызывает
    // экспортную функцию модуля объекта на сервере): functions.X в модуле
    // объекта (поставка/пользователь) исполняется в ObjectModuleRuntime
    // с русским API (Справочники/Файлы/ПарсерВыписки/...). Ядро НЕ знает
    // бизнес-кейсы — только механизм вызова.
    //   callServer('document', 'bank_statement_load', 'ЗагрузитьВыпискуНаСервере', { docId })
    //   → Promise<unknown> (возвращаемое значение функции, JSON)
    callServer(entityType: string, entityCode: string, functionName: string, args?: Record<string, unknown>): Promise<unknown>;

    // Диалог выбора ФАЙЛА: файл прикрепляется к объекту (владелец = recordId)
    // через FileService.UploadFile (существующий механизм) → колбэк с id файла.
    //   openFileDialog(callback: (fileId: string, fileName: string) => void): void;
    openFileDialog(callback: (fileId: string, fileName: string) => void): void;

    // Ошибка формы: показывается баннером ПОВЕРХ формы (поля остаются видимыми)
    setError(message: string): void;
  }


/**
 * Модуль формы — хранит обработчики и управляет выполнением
 */
export class FormModule {
  private clientHandlers: Map<string, Function> = new Map();
  private serverHandlers: Map<string, string> = new Map();
  private moduleCode: string = '';
  private formId: string = '';
  // Тела обработчиков (до компиляции): функции модуля компилируются в ЕДИНЫЙ
  // scope — как модуль в 1С: обработчики видят ДРУГ ДРУГА (вспомогательные
  // функции модуля вызываются из обработчиков).
  private clientBodies: Array<{ name: string; params: string; body: string }> = [];
  private serverBodies: Array<{ name: string; body: string }> = [];

  /**
   * Загрузить модуль формы из ZWPФормы
   */
  async load(formId: string): Promise<void> {
    this.formId = formId;
    try {
      const result = await commands.getFormCommands({ formId });
      this.moduleCode = (result as any).formModule || '';
      this.parseModule();
    } catch (e) {
      console.warn('Failed to load form module:', e);
    }
  }

  /**
   * Парсинг модуля формы — ДЕТЕРМИНИРОВАННЫЙ сканер (без регулярок-угадываний):
   * идём по исходнику слева направо, находим каждое `function` (границы слова),
   * читаем имя, параметры и тело ПО БАЛАНСУ ФИГУРНЫХ СКОБОК (с пропуском строк
   * и комментариев) — вложенные функции больше не обрезают обработчик.
   * Поддерживаемые форматы:
   *   function Имя(form, params) { ... }          — обычная функция
   *   Имя: function(form, params) { ... }          — свойство объекта
   *   // @context: server\n function Имя(...) {...} — серверный обработчик
   */
  private parseModule(): void {
    if (!this.moduleCode) return;
    const src = this.moduleCode;
    const n = src.length;

    const isIdent = (ch: string | undefined): boolean => !!ch && /[a-zA-Z0-9_а-яА-ЯёЁ]/.test(ch);
    const isWs = (ch: string | undefined): boolean => !!ch && /\s/.test(ch);
    const isIdentStart = (ch: string | undefined): boolean => !!ch && /[a-zA-Z_а-яА-ЯёЁ]/.test(ch);

    // Пропуск до конца строки/блока/строки-литерала (индекс первого символа ПОСЛЕ)
    const skipLine = (pos: number): number => { const e = src.indexOf('\n', pos); return e === -1 ? n : e + 1; };
    const skipBlock = (pos: number): number => { const e = src.indexOf('*/', pos + 2); return e === -1 ? n : e + 2; };
    const skipString = (pos: number, q: string): number => {
      let i = pos + 1;
      while (i < n) {
        if (src[i] === '\\') { i += 2; continue; }
        if (src[i] === q) return i + 1;
        i++;
      }
      return n;
    };

    // Тело функции: от позиции '{' до сбалансированной '}' (учитывая строки/комментарии)
    const readBody = (pos: number): { body: string; end: number } => {
      let depth = 1;
      let i = pos + 1;
      let bodyStart = i;
      while (i < n && depth > 0) {
        const c = src[i];
        if (c === '/' && src[i + 1] === '/') { i = skipLine(i); continue; }
        if (c === '/' && src[i + 1] === '*') { i = skipBlock(i); continue; }
        if (c === "'" || c === '"' || c === '`') { i = skipString(i, c); continue; }
        if (c === '{') depth++;
        else if (c === '}') {
          depth--;
          if (depth === 0) return { body: src.slice(bodyStart, i), end: i + 1 };
        }
        i++;
      }
      // Несбалансированная функция — не регистрируем (дефектный модуль)
      return { body: '', end: n };
    };

    // Параметры: от '(' до ')' (вне строк/комментариев)
    const readParams = (pos: number): { ok: boolean; params: string; end: number } => {
      let i = pos + 1;
      while (i < n) {
        const c = src[i];
        if (c === "'" || c === '"' || c === '`') { i = skipString(i, c); continue; }
        if (c === '/') {
          if (src[i + 1] === '/') { i = skipLine(i); continue; }
          if (src[i + 1] === '*') { i = skipBlock(i); continue; }
        }
        if (c === ')') return { ok: true, params: src.slice(pos + 1, i), end: i + 1 };
        i++;
      }
      return { ok: false, params: '', end: n };
    };

    // Имя функции: идентификатор после `function ` (или `Имя: function`)
    const readName = (fnPos: number): { name: string; fnAfterName: number } => {
      let i = fnPos + 8; // после 'function'
      while (i < n && isWs(src[i])) i++;
      if (isIdentStart(src[i])) {
        const s = i;
        while (i < n && isIdent(src[i])) i++;
        return { name: src.slice(s, i), fnAfterName: i };
      }
      // Анонимная (свойство объекта): идём назад от fnPos к `Имя:`
      let j = fnPos - 1;
      while (j >= 0 && isWs(src[j])) j--;
      if (j >= 0 && src[j] === ':') {
        j--;
        while (j >= 0 && isWs(src[j])) j--;
        const e = j + 1;
        while (j >= 0 && isIdent(src[j])) j--;
        const name = src.slice(j + 1, e);
        if (name && isIdentStart(name[0])) return { name, fnAfterName: fnPos + 8 };
      }
      return { name: '', fnAfterName: fnPos + 8 };
    };

    // Контекст: ближайший комментарий `// @context: X` ПЕРЕД функцией (до '}' или ';')
    const contextBefore = (fnPos: number): HandlerContext => {
      const prefix = src.slice(0, fnPos);
      const lastStmt = Math.max(prefix.lastIndexOf('}'), prefix.lastIndexOf(';'), prefix.lastIndexOf('\n'));
      const m = prefix.slice(lastStmt + 1).match(/\/\/\s*@context:\s*(\w+)\s*$/);
      return (m?.[1] as HandlerContext) || 'client';
    };

    let pos = 0;
    while (pos < n) {
      const fnIdx = src.indexOf('function', pos);
      if (fnIdx === -1) break;
      // Граница слова 'function' (не внутри идентификатора)
      if (fnIdx > 0 && isIdent(src[fnIdx - 1])) { pos = fnIdx + 8; continue; }
      const afterFn = src[fnIdx + 8];
      if (afterFn !== undefined && !isWs(afterFn) && afterFn !== '(' && afterFn !== '.') { pos = fnIdx + 8; continue; }
      const { name, fnAfterName } = readName(fnIdx);
      if (!name) { pos = fnAfterName; continue; }
      let i = fnAfterName;
      while (i < n && isWs(src[i])) i++;
      if (src[i] !== '(') { pos = fnAfterName; continue; }
      const params = readParams(i);
      if (!params.ok) break;
      i = params.end;
      while (i < n && isWs(src[i])) i++;
      if (src[i] !== '{') { pos = params.end; continue; }
      const body = readBody(i);
      if (!body.body) { pos = body.end; continue; }
      const ctx = contextBefore(fnIdx);
      if (ctx === 'server') {
        this.serverBodies.push({ name, body: body.body.trim() });
      } else {
        this.clientBodies.push({ name, params: params.params.trim(), body: body.body });
      }
      pos = body.end;
    }
    this.compile();
  }

  /**
   * Компиляция ВСЕХ клиентских обработчиков в ЕДИНЫЙ scope: вспомогательные
   * функции модуля видны из обработчиков (как модуль в 1С). Несбалансированный/
   * битый модуль — одна явная ошибка в консоли, обработчики не регистрируются.
   */
  private compile(): void {
    if (this.clientBodies.length === 0) return;
    let src = 'var __handlers = {};\n';
    for (const e of this.clientBodies) {
      // Сигнатура — КАК В ИСХОДНИКЕ (обработчик может принимать (form, params)
      // или вспомогательные аргументы, напр. (form, docId))
      src += `function ${e.name}(${e.params}) {\n${e.body}\n}\n`;
      src += `__handlers[${JSON.stringify(e.name)}] = ${e.name};\n`;
    }
    src += 'return __handlers;';
    try {
      const factory = new Function(src);
      const handlers = factory() as Record<string, Function>;
      this.clientHandlers = new Map(Object.entries(handlers));
    } catch (e) {
      console.warn('Failed to compile form module:', e);
    }
    this.serverHandlers = new Map(this.serverBodies.map((b) => [b.name, b.body]));
  }

  /**
   * Выполнить обработчик
   */
  async execute(
    handlerName: string,
    params: HandlerParams,
    form: FormAPI
  ): Promise<unknown> {
    // Проверяем клиентский обработчик
    const clientHandler = this.clientHandlers.get(handlerName);
    if (clientHandler) {
      return clientHandler(form, params);
    }

    // Проверяем серверный обработчик
    const serverHandler = this.serverHandlers.get(handlerName);
    if (serverHandler) {
      throw new Error(`Server handlers are not supported anymore: ${handlerName}`);
    }

    // Обработчик не найден
    console.warn(`Handler ${handlerName} not found`);
    return undefined;
  }

  /**
   * Проверить наличие обработчика
   */
  hasHandler(name: string): boolean {
    return this.clientHandlers.has(name) || this.serverHandlers.has(name);
  }

  /**
   * Получить список обработчиков
   */
  getHandlers(): string[] {
    return [...new Set([...this.clientHandlers.keys(), ...this.serverHandlers.keys()])];
  }
}

/**
 * Создать API формы
 */
export function createFormAPI(form: {
  values: Record<string, unknown>;
  setValues: (fn: (prev: Record<string, unknown>) => Record<string, unknown>) => void;
  setDisplayNames: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
  tsData: Record<string, Array<Record<string, unknown>>>;
  setTsData: (fn: (prev: Record<string, Array<Record<string, unknown>>>) => Record<string, Array<Record<string, unknown>>>) => void;
  objectType: string;
  objectName: string;
  recordId?: string;
  openPicker: (type: string, callback: (result: { id: string; name: string; objectType: string; objectName: string }) => void) => void;
  setError: (error: string | null) => void;
  onClose: () => void;
  setTitle?: (title: string) => void;
  setFieldVisible?: (name: string, visible: boolean) => void;
  onSave?: () => Promise<void>;
  onPost?: () => Promise<void>;
}): FormAPI {
  return {
    get objectType() { return form.objectType; },
    get objectName() { return form.objectName; },
    get recordId() { return form.recordId; },
    get values() { return form.values; },

    getField(name: string): unknown {
      return form.values[name];
    },

    setField(name: string, value: unknown): void {
      form.setValues((prev) => ({ ...prev, [name]: value }));
      form.setDisplayNames((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    },

    getRow(tableName: string, rowIndex: number): Record<string, unknown> | undefined {
      return form.tsData[tableName]?.[rowIndex];
    },

    addRow(tableName: string, data: Record<string, unknown>): void {
      form.setTsData((prev) => {
        const newData = { ...prev };
        if (!newData[tableName]) newData[tableName] = [];
        newData[tableName] = [...newData[tableName], data];
        return newData;
      });
    },

    deleteRow(tableName: string, rowIndex: number): void {
      form.setTsData((prev) => {
        const newData = { ...prev };
        if (newData[tableName]) {
          newData[tableName] = newData[tableName].filter((_, i) => i !== rowIndex);
        }
        return newData;
      });
    },

    clearTable(tableName: string): void {
      form.setTsData((prev) => {
        const newData = { ...prev };
        newData[tableName] = [];
        return newData;
      });
    },

    openPicker(type: string, callback: (result: { id: string; name: string; objectType: string; objectName: string }) => void): void {
      form.openPicker(type, callback);
    },

    setTitle(title: string): void {
      form.setTitle?.(title);
    },

    setFieldVisible(name: string, visible: boolean): void {
      form.setFieldVisible?.(name, visible);
    },

    close(): void {
      form.onClose();
    },

    refresh(): void {
      window.location.reload();
    },

    async save(): Promise<void> {
      await form.onSave?.();
    },

    async post(): Promise<void> {
      await form.onPost?.();
    },

    setError(message: string): void {
      form.setError(message);
    },

    // Вызов СЕРВЕРНОЙ функции модуля объекта (как 1С: модуль формы →
    // экспортная функция модуля объекта на сервере). Бизнес-логика — в
    // модуле (поставка/пользователь), ядро — механизм ModuleService.Call.
    async callServer(entityType: string, entityCode: string, functionName: string, args?: Record<string, unknown>): Promise<unknown> {
      const r: any = await getSDK().module.call({
        entityType,
        entityCode,
        functionName,
        argsJson: JSON.stringify(args || {}),
      });
      if (r?.error) throw new Error(r.error);
      return r?.resultJson ? JSON.parse(r.resultJson) : null;
    },

    // Диалог выбора файла: прикрепляет файл к объекту (FileService.UploadFile,
    // ownerId = текущая запись) → колбэк с id файла. Если запись не сохранена
    // (recordId нет) — файл прикрепляется без владельца (пользователь прикрепит
    // позже или вызовет после сохранения).
    // ВАЖНО: в WebView2 программный click по <input type=file> НЕ в DOM блокируется —
    // надёжный способ — label вокруг input (как в FileListTab): клик по label.
    openFileDialog(callback: (fileId: string, fileName: string) => void): void {
      const label = document.createElement('label');
      label.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;overflow:hidden;';
      const input = document.createElement('input');
      input.type = 'file';
      input.addEventListener('change', async () => {
        const file = input.files?.[0];
        label.remove();
        if (!file) return;
        try {
          // bytes proto → Uint8Array (НЕ base64-строка! «invalid uint32» при сериализации)
          const bytes = new Uint8Array(await file.arrayBuffer());
          const info: any = await getSDK().files.uploadFile({
            data: bytes as any,
            fileName: file.name,
            fileType: file.type || '',
            ownerId: form.recordId || '',
          } as any);
          callback(String(info?.id || ''), file.name);
        } catch (e) {
          form.setError(e instanceof Error ? e.message : String(e));
        }
      });
      label.appendChild(input);
      document.body.appendChild(label);
      label.click();
    },

    // РегистрСведенийМенеджерЗаписи (как в 1С): чтение/запись записи КОДОМ.
    // SSoT — сервер: физическая таблица регистра (ListTableData/Insert/Update).
    registerRecord: {
      async read(registerCode: string, dimensions: Record<string, unknown>): Promise<Record<string, unknown> | null> {
        const filters = Object.entries(dimensions).map(([field, value]) => ({ field, condition: '=', value: String(value) }));
        const r: any = await getSDK().metadata.listTableData({ schema: 'public', table: registerCode, pageSize: 1, page: 0, filters: filters as any });
        const row = r?.rows?.[0];
        if (!row) return null;
        const rec: Record<string, unknown> = { id: row.id };
        for (const v of row.values || []) rec[v.column] = v.value;
        // Заполняем значения формы (как после Прочитать() в 1С)
        form.setValues((prev) => ({ ...prev, ...rec }));
        return rec;
      },
      async write(registerCode: string, values: Record<string, unknown>): Promise<void> {
        // Измерения регистра (SSoT — структура с сервера): ключ записи
        const reg: any = await getSDK().metadata.getRegister({ id: registerCode });
        const dimCodes: string[] = ((reg?.dimensions || []) as any[]).map((d: any) => d.code || d.name).filter(Boolean);
        const filter = dimCodes
          .filter((d) => values[d] !== undefined && values[d] !== null && values[d] !== '')
          .map((d) => ({ field: d, condition: '=', value: String(values[d]) }));
        const payload = Object.entries(values)
          .filter(([k, v]) => k !== 'id' && !['created_at', 'updated_at', 'created_by', 'updated_by', 'is_marked'].includes(k) && v !== undefined && v !== null)
          .map(([column, value]) => ({ column, value: String(value) }));
        // Существующая запись по измерениям → Update, иначе Insert (upsert)
        const existing: any = filter.length > 0
          ? await getSDK().metadata.listTableData({ schema: 'public', table: registerCode, pageSize: 1, page: 0, filters: filter as any })
          : { rows: [] };
        const rowId = existing?.rows?.[0]?.id;
        if (rowId) {
          await getSDK().metadata.updateTableRow({ schema: 'public', table: registerCode, rowId, values: payload as any });
        } else {
          await getSDK().metadata.createTableRow({ schema: 'public', table: registerCode, values: payload as any });
        }
      },
      // Набор записей (РегистрСведенийНаборЗаписей): Прочитать() — строки по измерениям
      async readSet(registerCode: string, dimensions: Record<string, unknown>): Promise<Array<Record<string, unknown>>> {
        const filters = Object.entries(dimensions).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([field, value]) => ({ field, condition: '=', value: String(value) }));
        const r: any = await getSDK().metadata.listTableData({ schema: 'public', table: registerCode, pageSize: 1000, page: 0, filters: filters as any });
        const recs: Array<Record<string, unknown>> = [];
        for (const row of r?.rows || []) {
          const rec: Record<string, unknown> = { id: row.id };
          for (const v of row.values || []) rec[v.column] = v.value;
          // Представления ссылочных измерений (серверные {col}_presentation)
          for (const v of row.values || []) if (String(v.column).endsWith('_presentation')) rec[v.column] = v.value;
          recs.push(rec);
        }
        return recs;
      },
      // Набор записей: Записать() — ПОЛНАЯ ЗАМЕНА по измерениям (как в 1С):
      // записи с теми же значениями измерений удаляются, новые вставляются.
      async writeSet(registerCode: string, records: Array<Record<string, unknown>>, dimensions?: Record<string, unknown>): Promise<void> {
        // Измерения для отбора: явно переданные ИЛИ из первой записи набора
        let dims = dimensions;
        if (!dims && records.length > 0) {
          const reg: any = await getSDK().metadata.getRegister({ id: registerCode });
          const dimCodes: string[] = ((reg?.dimensions || []) as any[]).map((d: any) => d.code || d.name).filter(Boolean);
          dims = {};
          for (const d of dimCodes) if (records[0][d] !== undefined && records[0][d] !== null && records[0][d] !== '') dims[d] = records[0][d];
        }
        // 1. Удалить существующие записи с этими измерениями (пустой набор = очистка)
        const dimsPresent = dims ? Object.entries(dims).filter(([, v]) => v !== undefined && v !== null && v !== '') : [];
        if (dimsPresent.length > 0) {
          const filters = dimsPresent.map(([field, value]) => ({ field, condition: '=', value: String(value) }));
          const existing: any = await getSDK().metadata.listTableData({ schema: 'public', table: registerCode, pageSize: 1000, page: 0, filters: filters as any });
          for (const row of existing?.rows || []) {
            await getSDK().metadata.deleteTableRow({ schema: 'public', table: registerCode, rowId: row.id });
          }
        }
        // 2. Вставить новые строки
        for (const rec of records) {
          const payload = Object.entries(rec)
            .filter(([k, v]) => k !== 'id' && !['created_at', 'updated_at', 'created_by', 'updated_by', 'is_marked'].includes(k) && !String(k).endsWith('_presentation') && v !== undefined && v !== null)
            .map(([column, value]) => ({ column, value: String(value) }));
          await getSDK().metadata.createTableRow({ schema: 'public', table: registerCode, values: payload as any });
        }
      },
    },
  };
}

